import { Card, CardContent, Chip, Grid, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@common/Modal/Modal";
import { IPaymentMethod } from "../interface/IPaymentMethod";
import "./css/PaymentCardItem.scss";
import { ExpiryDateCardForm } from "./ExpiryDateCardForm";
import { format, parse } from "date-fns";
import { API_DATE_FORMAT } from "@constants";

interface IParams {
    card: IPaymentMethod;
    isIframeDisplayed: boolean;
    closeIframe(): void;
    removeCard(cardId: number): void;
    setDefaultCard(cardId: number): void;
    accountId: string;
}

export const PaymentCardItem = (props: IParams) => {
    const { card, removeCard, setDefaultCard, isIframeDisplayed, closeIframe, accountId } = props;

    const [isEditExpiry, setIsEditExpiry] = useState(false);
    const [expiryDate, setExpiryDate] = useState(card.expirationDate);
    const [hasConfirmModal, setHasConfirmModal] = useState(false);
    const { t } = useTranslation("payment");

    let cardTypeLogoSrc;

    try {
        cardTypeLogoSrc = require(`../../../static/img/paymentCards/${card.type.toUpperCase()}.png`);
    } catch (e) {
        cardTypeLogoSrc = "error";
    }

    const cardPartialDigits = card.partialDigits.match(/.{1,4}/g) ?? [];
    const expiryDateFormatted = format(parse(expiryDate, API_DATE_FORMAT, new Date()), "MMM-yy");

    const editCard = () => {
        setIsEditExpiry(true);
        closeIframe();
    };

    useEffect(() => {
        if (isIframeDisplayed) {
            setIsEditExpiry(false);
        }
    }, [isIframeDisplayed]);

    return (
        <div className="payment-card-container">
            <Modal
                close={() => setHasConfirmModal(false)}
                confirm={() => removeCard(card.paymentMethodId)}
                isModalOpen={hasConfirmModal}
                title={t("deletePayment")}
            >
                <Typography align="center" variant="normal">
                    {t("cardDeleteMessage")}
                </Typography>
            </Modal>
            <Card className="payment-card-wrapper">
                <CardContent>
                    <Grid container spacing={4}>
                        <Grid
                            className="payment-card__type"
                            item
                            style={{ justifyContent: card.default ? "space-between" : "flex-end" }}
                            xs={12}
                        >
                            {card.default && (
                                <Chip color="primary" icon={<CheckIcon fontSize="small" />} label={t("defaultCard")} />
                            )}
                            <img alt={card.type} className="payment-card__image" src={cardTypeLogoSrc} />
                        </Grid>
                        <Grid className="payment-card__infos" item xs={12}>
                            {cardPartialDigits.map((cardDigits, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Typography color="inherit" key={index} variant="normal">
                                    {cardDigits}
                                </Typography>
                            ))}
                        </Grid>
                        <Grid className="payment-card__infos" item xs={12}>
                            <Typography color="inherit" variant="normal">
                                {card.cardholderName}
                            </Typography>
                            <Typography color="inherit" variant="normal">
                                {expiryDateFormatted}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {!isEditExpiry && (
                <List className="payment-card__actions" component="nav">
                    <ListItem button onClick={editCard}>
                        <ListItemIcon>
                            <EditIcon />
                        </ListItemIcon>
                        <ListItemText primary={t("editExpiry")} />
                    </ListItem>
                    {!card.default && (
                        <Fragment>
                            <ListItem button onClick={() => setHasConfirmModal(true)}>
                                <ListItemIcon>
                                    <DeleteIcon />
                                </ListItemIcon>
                                <ListItemText primary={t("remove")} />
                            </ListItem>
                            <ListItem button onClick={() => setDefaultCard(card.paymentMethodId)}>
                                <ListItemIcon>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText color="inherit" primary={t("setDefault")} />
                            </ListItem>
                        </Fragment>
                    )}
                </List>
            )}
            {isEditExpiry && (
                <ExpiryDateCardForm
                    accountId={accountId}
                    cardId={card.paymentMethodId}
                    finishEdit={() => setIsEditExpiry(false)}
                    setExpiryDate={setExpiryDate}
                />
            )}
        </div>
    );
};
